import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import './App.css';

// docs/futuregoals.md FG-8: next-themes' <ThemeProvider> used to wrap the app
// here, but nothing consumes it anymore (sonner.jsx reads the real theme from
// AuthContext; Shell's useTheme() call was dead code). Worse, on mount it wrote
// class="light"/"dark" onto <html> from the OS setting, racing AuthContext's
// theme-apply effect and stripping the `dark` class for anyone whose OS is
// light but whose chosen theme (e.g. Obsidian Gold) is dark — which broke
// Shell's `dark:bg-background/50` and washed the page grey until you re-picked
// a theme in Settings. AuthContext is the sole theme authority now.
import { AuthProvider } from './contexts/AuthContext';
// docs/futuregoals.md FG-8: was `import { Toaster } from 'sonner'` (the raw
// package, no theme wired up at all — see components/ui/sonner.jsx's own
// comment for the full history). Must render inside AuthProvider so it can
// read the real applied theme, which is already true of where it's used
// below.
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Shell from './components/layout/Shell';
import ErrorBoundary from './components/common/ErrorBoundary';

// Lazy Loaded Pages
const DashboardIndex = lazy(() => import('./pages/Dashboard'));
const HubPage = lazy(() => import('./pages/HubPage'));
const WritersRoom = lazy(() => import('./pages/WritersRoom'));
const SubscriptionPlan = lazy(() => import('./pages/SubscriptionPlan'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const PasswordRecovery = lazy(() => import('./pages/PasswordRecovery'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const BillingSuccess = lazy(() => import('./pages/BillingSuccess'));
const BillingCancel = lazy(() => import('./pages/BillingCancel'));

const PageLoader = () => (
  <div className="h-screen w-full flex items-center justify-center bg-background/50 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="w-12 h-12 rounded-full border-4 border-primary/20" />
        <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground animate-pulse">Initializing Interface</p>
    </div>
  </div>
);

const ComponentLoader = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
    <Loader2 className="w-10 h-10 animate-spin text-premium" />
    <p className="text-xs font-black uppercase tracking-widest animate-pulse opacity-50">Loading Module...</p>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
            {/* ... routes ... */}
            <Route path="/auth/login" element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
            <Route path="/auth/signup" element={<Suspense fallback={<PageLoader />}><SignupPage /></Suspense>} />
            <Route path="/auth/verify" element={<Suspense fallback={<PageLoader />}><VerifyEmail /></Suspense>} />
            <Route path="/auth/recovery" element={<Suspense fallback={<PageLoader />}><PasswordRecovery /></Suspense>} />
            <Route path="/auth/reset-password" element={<Suspense fallback={<PageLoader />}><PasswordRecovery /></Suspense>} />
            <Route path="/auth/callback" element={<Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>} />

            {/* Public Info Routes */}
            <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsPage /></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPage /></Suspense>} />
            <Route path="/help" element={<Suspense fallback={<PageLoader />}><SupportPage /></Suspense>} />

            {/* Ecosystem Central Hub */}
            <Route path="/hub" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <HubPage />
                </Suspense>
              </ProtectedRoute>
            } />

            {/* Protected Routes wrapped in Shell */}
            <Route path="/" element={
              <ProtectedRoute>
                <Navigate to="/hub" replace />
              </ProtectedRoute>
            } />

            {/*
              finding D-02: requireVerified is applied to the generation-capable
              routes now that finding C-04 (server-side email-verification
              enforcement) has landed — this is defence in depth, not the
              authorization boundary itself; the backend gate is authoritative.
            */}
            {/*
              docs/futuregoals.md FG-6: requirePubNcnUnlocked gates the
              actual "using the product" surfaces when an admin has locked
              PUB-NCN for maintenance/an incident — same defence-in-depth
              tier as requireVerified above. The Hub and /admin deliberately
              do NOT carry this prop (the Hub is the launcher, /admin is the
              only place to undo the lock), and neither does /subscription
              or /billing/* (account/billing management should stay usable
              even while the core product is locked).
            */}
            <Route path="/dashboard" element={
              <ProtectedRoute requireVerified requirePubNcnUnlocked>
                <Shell>
                  <Suspense fallback={<ComponentLoader />}>
                    <DashboardIndex />
                  </Suspense>
                </Shell>
              </ProtectedRoute>
            } />

            <Route path="/writers-room/:generationId" element={
              <ProtectedRoute requireVerified requirePubNcnUnlocked>
                <Shell>
                  <Suspense fallback={<ComponentLoader />}>
                    <WritersRoom />
                  </Suspense>
                </Shell>
              </ProtectedRoute>
            } />

            <Route path="/writers-room" element={
              <ProtectedRoute requireVerified requirePubNcnUnlocked>
                <Shell>
                  <Suspense fallback={<ComponentLoader />}>
                    <WritersRoom />
                  </Suspense>
                </Shell>
              </ProtectedRoute>
            } />

            <Route path="/subscription" element={
              <ProtectedRoute>
                <Shell>
                  <Suspense fallback={<ComponentLoader />}>
                    <SubscriptionPlan />
                  </Suspense>
                </Shell>
              </ProtectedRoute>
            } />

            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <Shell>
                  <Suspense fallback={<ComponentLoader />}>
                    <AdminPage />
                  </Suspense>
                </Shell>
              </ProtectedRoute>
            } />

            <Route path="/billing/success" element={
              <ProtectedRoute>
                <Shell>
                  <Suspense fallback={<ComponentLoader />}>
                    <BillingSuccess />
                  </Suspense>
                </Shell>
              </ProtectedRoute>
            } />

            <Route path="/billing/cancel" element={
              <ProtectedRoute>
                <Shell>
                  <Suspense fallback={<ComponentLoader />}>
                    <BillingCancel />
                  </Suspense>
                </Shell>
              </ProtectedRoute>
            } />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/hub" replace />} />
          </Routes>
        </ErrorBoundary>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;